import DOMPurify from "dompurify";
import { FC, useEffect, useState } from "react";

import ActiveForm from "web/Pages/Checkout/Subscription/BenefitSummary/ActiveForm";

import ErrorComponent from "web/Layout/Common/ErrorComponent";
import Loading from "web/Layout/Common/Loading";
import __ from "web/Layout/Translations";

import Title from "web/Components/Common/Title";
import withScrollToTop from "web/Components/Common/withScrollToTop";

import parseObjectToGetParameters from "web/utils/data/parser/object/parseObjectToGetParameters";
import newRelicErrorReport from "web/utils/system/essentials/newRelicErrorReport";

import restUrls from "web/constants/restUrls";
import urls from "web/constants/urls";

import { request } from "web/api";

import { IApiRequestMethods, IRequestOpts } from "web/api/apiRequestTypes";
import { IActiveFormValues, SubscriptionActiveFormData } from "web/types/Subscription";
import classes from "./externalform.scss";

const infoClasses = { header: classes.infoTitle };
const titleClasses = { header: classes.title };

type IForm = {
  name: string;
  description: string;
  data: SubscriptionActiveFormData[]
} | null;

type IErrorData = {
  response: {
    status: number;
  }
};

const ExternalForm: FC<{ token: string, subscriptionItemId: string }> = ({ token, subscriptionItemId }) => {
  const [form, setForm] = useState<IForm>(null);
  const [completedInfo, setCompletedInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<boolean | IErrorData>(false);

  useEffect(() => {
    const fetchData = async (requestUrl: string, options: IRequestOpts, setData: (arg: IForm) => void) => {
      setLoading(true);
      try {
        const response = await request(requestUrl, options) as IForm;
        setData(response);
      } catch (errorData) {
        newRelicErrorReport(
          errorData,
          "web-app/web/Pages/ExternalForm/externalForm.js - 55"
        );
        switch ((errorData as IErrorData).response.status) {
          case 404:
            window.history.pushState("", "", `${urls.home}`);
            break;
          case 410:
            setCompletedInfo(__("Formularz został wypełniony."));
            break;
          default:
            setError(errorData as IErrorData);
        }
      } finally {
        setLoading(false);
      }
    };
    const params = parseObjectToGetParameters({
      subscriptionItemId,
      token,
    });
    fetchData(
      `${restUrls.externalActiveForms}?${params}`,
      {
        omitToken: true,
      },
      setForm
    );
  }, []);

  const prepareFields = (fields: IActiveFormValues) => {
    const notEmptyFields = fields;
    Object.keys(notEmptyFields).forEach((key) => {
      if (!notEmptyFields[key]) {
        delete notEmptyFields[key];
      }
    });

    return notEmptyFields;
  };

  const submitExternalForm = async (formValues: IActiveFormValues) => {
    const submitData = {
      fields: prepareFields(formValues),
      subscriptionItemId,
      token,
    };
    try {
      await request(restUrls.externalActiveForms, {
        method: IApiRequestMethods.POST,
        body: JSON.stringify(submitData),
        omitToken: true,
      });
      setCompletedInfo(__("Formularz został wysłany."));
    } catch (err) {
      newRelicErrorReport(
        err,
        "web-app/web/Pages/ExternalForm/externalForm.js - 111"
      );
      console.error(err);
      setError(err as IErrorData);
    }
  };

  let formContainer;
  switch (true) {
    case !!error: {
      console.error(error);
      formContainer = <ErrorComponent />;
      break;
    }
    case !!loading: {
      formContainer = <Loading />;
      break;
    }
    case !!completedInfo: {
      formContainer = (
        <>
          <Title name={completedInfo} classes={infoClasses} />
        </>
      );
      break;
    }
    default: {
      formContainer = form ? (
        <div className={classes.form}>
          <Title name={form.name} classes={titleClasses} />
          <div
            className="cke_editable"
            // eslint-disable-next-line
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(form.description),
            }}
          />
          <section className={classes.content}>
            <ActiveForm data={form.data} onSubmit={submitExternalForm} close={() => {}} />
          </section>
        </div>
      ) : null;
    }
  }

  return (
    <article className={classes.root}>
      <div className={classes.wrapper}>{formContainer}</div>
    </article>
  );
};

export default withScrollToTop(ExternalForm);
